import { t } from '@lingui/macro';
import { InputField } from 'components/FormFields/InputField';
import { SelectField } from 'components/FormFields/SelectField';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

/** Sentinel select value that reveals the free-text field. */
export const LIQUIDATION_REASON_OTHER = 'other';

export const liquidationReasonOptions = () => [
  {
    label: t`Liquidatie`,
    value: 'Liquidatie',
  },
  {
    label: t`Fusie`,
    value: 'Fusie',
  },
  {
    label: t`Splitsing`,
    value: 'Splitsing',
  },
  {
    label: t`Overplaatsing naar buitenland`,
    value: 'Overplaatsing naar buitenland',
  },
  {
    label: t`Aangepaste reden`,
    value: LIQUIDATION_REASON_OTHER,
  },
];

const getLiquidationReasonFormSchema = () => {
  return yup.object({
    reasonSelect: yup.string().required(),
    // Only the custom option needs free text; for the preset options the option is the reason.
    reason: yup.string().when('reasonSelect', {
      is: (value: any) => value === LIQUIDATION_REASON_OTHER,
      then: yup.string().trim().required(),
      otherwise: yup.string().trim().nullable(),
    }),
  });
};

export const liquidationReasonFormSchema = getLiquidationReasonFormSchema();

export type LiquidationReasonFormValues = yup.InferType<typeof liquidationReasonFormSchema>;

/**
 * The reason is stored as a single string, so a saved value has to be resolved back into the
 * select/free-text pair the form works with.
 */
export const liquidationReasonDefaults = (reason?: string | null) => {
  if (!reason) return { reasonSelect: undefined, reason: '' };

  const isPresetReason = liquidationReasonOptions().some(
    (option) => option.value !== LIQUIDATION_REASON_OTHER && option.value === reason,
  );
  return isPresetReason
    ? { reasonSelect: reason, reason: '' }
    : { reasonSelect: LIQUIDATION_REASON_OTHER, reason };
};

/** Folds the select/free-text pair back into the single stored reason. */
export const toLiquidationReason = ({
  reasonSelect,
  reason,
}: {
  reasonSelect?: string;
  reason?: string | null;
}) => (reasonSelect === LIQUIDATION_REASON_OTHER ? reason : reasonSelect);

const LiquidationReasonForm = () => {
  const { watch } = useFormContext();
  const isReasonOther = watch('reasonSelect') === LIQUIDATION_REASON_OTHER;

  return (
    <>
      <SelectField
        required
        label={t`Reden voor beëindiging`}
        name="reasonSelect"
        options={liquidationReasonOptions()}
        placeholder={t`Kies een reden`}
      />

      {isReasonOther && (
        <InputField
          required
          label={t`Aangepaste reden voor beëindiging`}
          name="reason"
          placeholder={t`Aangepaste reden voor beëindiging`}
          rows={3}
          spaceAfter={6}
          type="textarea"
        />
      )}
    </>
  );
};

export { LiquidationReasonForm };
